import { Menu } from "components/common/Menu";
import { IconButton, Tooltip } from "@mui/material";
import useCurrentUserPermission from "hooks/useCurrentUserPermission";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Asset } from "types/asset";
import { History, CheckCircle, LockClock } from "@mui/icons-material";
import useFeatureFlag from "hooks/useFeatureFlag";
import { GridRowId } from "@mui/x-data-grid-pro";
import { api } from "services/api.service";

function openCertificate(asset: Asset) {
  const popup = window.open("about:blank", "_blank");
  if (!popup) return;
  popup.opener = null;

  api
    .get<{ url: string }>(`/assets/${asset.uuid}/certificate_of_destruction`)
    .then(({ data }) => {
      if (popup.closed || !data.url) {
        if (!popup.closed) popup.close();
        return;
      }
      popup.location.replace(data.url);
    })
    .catch(() => {
      if (!popup.closed) popup.close();
    });
}

type ActionCellProps = {
  asset: Asset;
  refetch: () => void;
  setSecurityLocksOpen: (open: boolean) => void;
  setSelectedAssetIds: (assetIds: GridRowId[]) => void;
  setSelectAllFlag: (selectAllFlag: boolean) => void;
  setNotFoundOpen: (open: boolean) => void;
};

export function ActionCell({
  asset,
  refetch,
  setSecurityLocksOpen,
  setSelectedAssetIds,
  setSelectAllFlag,
  setNotFoundOpen,
}: ActionCellProps) {
  const { hasPermission } = useCurrentUserPermission();
  const { enabled: isEnrollmentV2Enabled } = useFeatureFlag("enrollment_v2");

  return (
    <>
      <Menu
        button={(props) => (
          <Tooltip
            title={
              hasPermission("assets")
                ? ""
                : "You do not have permissions for this action, please contact your administrator for permission."
            }
          >
            <span>
              <IconButton {...props} disabled={!hasPermission("assets")}>
                <MoreHorizIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        actions={[
          {
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <History fontSize="small" color="disabled" />
                View Asset History
              </span>
            ),
            to: `/assets/${asset.uuid}/history`,
          },
          ...(asset.blocksClientNotFound
            ? []
            : [
                {
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <LockClock fontSize="small" color="disabled" />
                      Can't release?
                    </span>
                  ),
                  onClick: () => {
                    setNotFoundOpen(true);
                    setSelectAllFlag(false);
                    setSelectedAssetIds([asset.id]);
                  },
                },
              ]),
          ...(isEnrollmentV2Enabled && ["enrolled", "locked"].includes(asset.enrolledStatus?.toLowerCase() ?? "")
            ? [
                {
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <CheckCircle fontSize="small" color="disabled" />
                      Confirm Released
                    </span>
                  ),
                  onClick: () => {
                    setSecurityLocksOpen(true);
                    setSelectAllFlag(false);
                    setSelectedAssetIds([asset.id]);
                  },
                },
              ]
            : []),
          ...(asset.certificateOfDestructionAttached
            ? [
                {
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <CheckCircle fontSize="small" color="disabled" />
                      Download COD
                    </span>
                  ),
                  onClick: () => {
                    openCertificate(asset);
                  },
                },
              ]
            : []),
        ]}
      />
    </>
  );
}
